import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Icons from '../components/Icons';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { downloadFile } from '../utils/downloadFile';
import AspectRatioSelector from '../components/AspectRatioSelector';
import {
  downloadMultiShotZip,
  fetchMultiShotJob,
  fetchLatestMultiShotJob,
  generateMultiShot,
  openMultiShotEvents,
} from '../services/api';

const CREDIT_COST = 140;
const STYLES = [
  { id: 'cinematic1', label: 'CINEMATIC 1' },
  { id: 'cinematic2', label: 'CINEMATIC 2' },
  { id: 'aesthetic1', label: 'AESTHETIC 1' },
  { id: 'normalShots1', label: 'NORMAL SHOTS 1' },
  { id: 'gameShots1', label: 'GAME SHOTS 1' },
  { id: 'actionShots1', label: 'ACTION SHOTS 1' },
  { id: 'techShots1', label: 'TECH SHOTS 1' },
  { id: 'animalShots1', label: 'ANIMAL SHOTS 1' },
];
const RATIOS = ['16:9', '9:16'];
const RESOLUTIONS = ['3840 x 2160', '2160 x 3840'];
const STORAGE_KEY = 'aifield_multishot_active_job';
const STYLE_SHOT_LABELS = {
  cinematic1: ['Medium Close-Up', 'Two-Shot Mirror', 'High Angle Overhead', 'Extreme Wide Night', "Worm's Eye", 'Close-Up Profile', 'Rack Focus Foreground', 'Slow Motion Freeze', 'Over-Shoulder Reverse'],
  cinematic2: ['Steadicam Follow', 'Extreme Close-Up Hands', 'Aerial Crane', 'Dutch Angle Close', 'Point of View', 'Silhouette Interior', 'Telephoto Compression', 'Low Angle Environment', 'Insert Detail'],
  aesthetic1: ['Golden Hour Wide', 'Puddle Reflection', 'Window Light Portrait', 'Through Foliage', 'Overhead Flat Lay', 'Lens Flare Close-Up', 'Golden Telephoto', 'Leading Lines Wide', 'Doorway Silhouette'],
  normalShots1: ['Establishing Wide', 'Shoulder Medium', 'Cowboy Shot', 'Clean Close-Up', 'Full Body Neutral', 'Walking Side', 'Over-Shoulder Clean', 'Two-Shot Equal', 'Ground Upward Tilt'],
  gameShots1: ['Third-Person Cam', 'First-Person View', 'Isometric High Angle', 'Side Scroll Flat', 'Boss Reveal Low', 'Tactical Overhead', 'Cutscene Close', 'Environment Wide', 'Defeat Slow Motion'],
  actionShots1: ['Running Lateral', 'Impact Close', 'Aerial Pursuit', 'Ground Charge', 'Dutch Chaos', 'Freeze Frame', 'Heroic Sprint', 'Action POV', 'Wide Chaos'],
  techShots1: ['Hero Product Wide', 'Macro Circuit', 'Overhead Workspace', 'Screen Glow Portrait', 'Glass Reflection', 'Interface Close-Up', 'Device Monument', 'DOF Stack', 'Dark Studio'],
  animalShots1: ['Wildlife Wide', 'Extreme Eye Close-Up', 'Ground Eye Level', 'Aerial Tracking', 'Profile Telephoto', 'Motion Blur Pursuit', 'Golden Silhouette', 'Behavior Medium', 'Environmental Portrait'],
};

function resolutionForRatio(ratio) {
  return ratio === '9:16' ? '2160 x 3840' : '3840 x 2160';
}

function ratioForResolution(resolution) {
  return resolution === '2160 x 3840' ? '9:16' : '16:9';
}

function normalizeRatio(ratio, resolution) {
  if (RATIOS.includes(ratio)) return ratio;
  return ratioForResolution(resolution);
}

function normalizeResolution(resolution, ratio = '16:9') {
  return RESOLUTIONS.includes(resolution) ? resolution : resolutionForRatio(ratio);
}

function normalizeStyle(style) {
  return STYLES.some(item => item.id === style) ? style : 'cinematic1';
}

function getShotLabel(style, panel, idx) {
  return STYLE_SHOT_LABELS[normalizeStyle(style)]?.[idx] || panel.label || `Shot ${String(idx + 1).padStart(2, '0')}`;
}

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Invalid image file'));
    };
    img.src = url;
  });
}

export default function MultiShotPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, refreshItems } = useCredits();
  const navigate = useNavigate();

  const [sourceFile, setSourceFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [style, setStyle] = useState('cinematic1');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('3840 x 2160');
  const [phase, setPhase] = useState('idle');
  const [jobId, setJobId] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 9, panels: [] });
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [fullscreenPanel, setFullscreenPanel] = useState(null);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  const fileRef = useRef(null);
  const eventRef = useRef(null);
  const pollRef = useRef(null);
  const genLockRef = useRef(false);
  const completionToastRef = useRef(false);

  const isBusy = phase === 'creating_grid' || phase === 'cropping';
  const isComplete = phase === 'completed';
  const canGenerate = !!sourceFile && !!style && !!aspectRatio && !!resolution && !isBusy;
  const panels = progress.panels || [];
  const resultPanels = panels.filter(panel => panel.url);

  const shotAspect = useMemo(() => {
    if (aspectRatio === '9:16') return '9 / 16';
    return '16 / 9';
  }, [aspectRatio]);

  const clearEvents = useCallback(() => {
    if (eventRef.current) {
      eventRef.current.close();
      eventRef.current = null;
    }
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const rememberJob = useCallback((id) => {
    if (!id) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ jobId: id, savedAt: Date.now() }));
    } catch {}
  }, []);

  const forgetJob = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  useEffect(() => () => {
    clearEvents();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [clearEvents, previewUrl]);

  const applySnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    setProgress(prev => ({ ...prev, ...snapshot }));
    if (snapshot.step === 'creating_grid' || snapshot.status === 'creating_grid') {
      setPhase('creating_grid');
    }
    if (snapshot.step === 'cropping' || snapshot.status === 'cropping') {
      setPhase('cropping');
    }
    if (snapshot.status === 'completed' || snapshot.step === 'completed') {
      setPhase('completed');
      clearEvents();
      if (!completionToastRef.current) {
        completionToastRef.current = true;
        toast.success('MultiShot ready');
      }
      if (refreshItems) refreshItems(true);
    }
    if (snapshot.status === 'failed' || snapshot.step === 'failed') {
      setPhase('failed');
      setError(snapshot.error || 'MultiShot failed');
      clearEvents();
    }
  }, [clearEvents, refreshItems]);

  const startPolling = useCallback((id) => {
    if (!token) return;
    const poll = async () => {
      try {
        const snapshot = await fetchMultiShotJob(token, id);
        applySnapshot(snapshot);
        if (snapshot.status !== 'completed' && snapshot.status !== 'failed') {
          pollRef.current = setTimeout(poll, 3000);
        }
      } catch {
        pollRef.current = setTimeout(poll, 5000);
      }
    };
    pollRef.current = setTimeout(poll, 1500);
  }, [applySnapshot, token]);

  const connectEvents = useCallback((id) => {
    clearEvents();
    if (!token) return;
    eventRef.current = openMultiShotEvents(
      token,
      id,
      applySnapshot,
      () => {
        if (eventRef.current) {
          eventRef.current.close();
          eventRef.current = null;
        }
        startPolling(id);
      }
    );
  }, [applySnapshot, clearEvents, startPolling, token]);

  const resumeJob = useCallback(async (id, { silent = false } = {}) => {
    if (!token || !id) return false;
    try {
      const snapshot = await fetchMultiShotJob(token, id);
      setJobId(snapshot.jobId);
      const nextResolution = normalizeResolution(snapshot.resolution, snapshot.aspectRatio);
      setResolution(nextResolution);
      setAspectRatio(normalizeRatio(snapshot.aspectRatio, nextResolution));
      setStyle(normalizeStyle(snapshot.style));
      setSourceFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
      rememberJob(snapshot.jobId);
      applySnapshot(snapshot);
      if (snapshot.status !== 'completed' && snapshot.status !== 'failed') {
        connectEvents(snapshot.jobId);
        if (!silent) toast('Reconnected to your MultiShot job');
      }
      return true;
    } catch {
      return false;
    }
  }, [applySnapshot, connectEvents, previewUrl, rememberJob, token]);

  useEffect(() => {
    if (!token || jobId || sourceFile) return;
    let cancelled = false;
    const boot = async () => {
      let storedId = '';
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        storedId = stored.jobId || '';
      } catch {}

      if (storedId && !cancelled) {
        const ok = await resumeJob(storedId, { silent: true });
        if (ok || cancelled) return;
        forgetJob();
      }

      if (!cancelled) {
        try {
          const latest = await fetchLatestMultiShotJob(token);
          if (latest?.jobId && !cancelled) {
            setJobId(latest.jobId);
            const nextResolution = normalizeResolution(latest.resolution, latest.aspectRatio);
            setResolution(nextResolution);
            setAspectRatio(normalizeRatio(latest.aspectRatio, nextResolution));
            setStyle(normalizeStyle(latest.style));
            rememberJob(latest.jobId);
            applySnapshot(latest);
            if (latest.status !== 'completed' && latest.status !== 'failed') connectEvents(latest.jobId);
          }
        } catch {}
      }
    };
    boot();
    return () => { cancelled = true; };
  }, [applySnapshot, connectEvents, forgetJob, jobId, rememberJob, resumeJob, sourceFile, token]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Use a JPG or PNG image');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be 10 MB or smaller');
      return;
    }
    try {
      const dims = await getImageDimensions(file);
      if (dims.width < 512 || dims.height < 512) {
        toast.error('Image must be at least 512x512');
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setSourceFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
      setPhase('idle');
      setProgress({ current: 0, total: 9, panels: [] });
      setJobId('');
      completionToastRef.current = false;
      clearEvents();
    } catch (err) {
      toast.error(err.message || 'Invalid image file');
    }
  }, [clearEvents, previewUrl]);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleGenerate = async () => {
    if (genLockRef.current) return;
    if (!user) {
      if (onShowAuth) onShowAuth();
      return;
    }
    if (credits < CREDIT_COST) {
      toast.error('Not enough credits');
      navigate('/subscription');
      return;
    }
    if (!sourceFile) {
      toast.error('Upload an image first');
      return;
    }

    genLockRef.current = true;
    setPhase('creating_grid');
    setShowMobilePreview(true);
    setError('');
    setProgress({ current: 0, total: 9, panels: [] });
    completionToastRef.current = false;

    try {
      const res = await generateMultiShot(token, { sourceImage: sourceFile, aspectRatio, resolution, style });
      setJobId(res.jobId);
      rememberJob(res.jobId);
      applySnapshot(res);
      if (res.creditsRemaining !== undefined && setCredits) setCredits(res.creditsRemaining);
      if (res.status !== 'completed' && res.status !== 'failed') connectEvents(res.jobId);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'MultiShot failed';
      setPhase('failed');
      setError(msg);
      toast.error(msg);
    } finally {
      genLockRef.current = false;
    }
  };

  const downloadZip = async () => {
    if (!jobId) return;
    try {
      await downloadMultiShotZip(token, jobId);
      toast.success('ZIP downloaded');
    } catch {
      toast.error('ZIP download failed');
    }
  };

  const removeSource = (event) => {
    event.stopPropagation();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSourceFile(null);
    setPreviewUrl('');
    setPhase('idle');
    setProgress({ current: 0, total: 9, panels: [] });
    setJobId('');
    setError('');
    forgetJob();
    clearEvents();
  };

  return (
    <>
      {fullscreenPanel && (
        <div className="img-fs" onClick={() => setFullscreenPanel(null)}>
          <img src={fullscreenPanel.url} alt={fullscreenPanel.label} className="img-fs-img" onClick={e => e.stopPropagation()} />
          <button className="img-fs-close" onClick={() => setFullscreenPanel(null)}><Icons.X sz={16} /></button>
        </div>
      )}

      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        {showMobilePreview ? <Icons.X sz={18} /> : <Icons.Grid sz={18} />}
      </button>

      <section className="panel">
        <div className="phead"><Icons.Clapperboard sz={16} /> MultiShot</div>

        <div className="psec">
          <div className="slbl">Image Upload <span className="opt">JPG/PNG, max 10 MB</span></div>
          <div
            className={`uzone ms-upload ${sourceFile ? 'on' : ''} ${dragging ? 'drag' : ''}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            {previewUrl ? (
              <div className="ms-upload-preview">
                <img src={previewUrl} alt="" />
                <div className="ms-upload-meta">
                  <span>{sourceFile?.name}</span>
                  <button type="button" onClick={removeSource}><Icons.X sz={12} /></button>
                </div>
              </div>
            ) : (
              <div className="ms-upload-empty">
                <Icons.Upload sz={22} />
                <span>Drop image here or choose file</span>
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png"
            hidden
            onChange={e => handleFile(e.target.files?.[0])}
          />
        </div>

        <div className="psec">
          <div className="slbl">Style</div>
          <select className="ms-select" value={style} onChange={e => setStyle(e.target.value)}>
            {STYLES.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </div>

        <div className="psec">
          <div className="slbl">Aspect Ratio</div>
          <AspectRatioSelector
            options={RATIOS}
            value={aspectRatio}
            onChange={ratio => {
              setAspectRatio(ratio);
              setResolution(resolutionForRatio(ratio));
            }}
          />
        </div>



        <div className="gen-sticky">
          <div className="cost-row">
            <span className="cost-l">Cost</span>
            <span className="cost-v">
              {CREDIT_COST} cr
              {' • '}{Math.max(0, credits - CREDIT_COST)} left
            </span>
          </div>
          <button className={`gbtn ${isBusy ? 'ld' : ''}`} onClick={handleGenerate} disabled={!canGenerate || credits < CREDIT_COST}>
            <Icons.Clapperboard sz={16} /> {isBusy ? 'Generating...' : 'Generate'}
          </button>
          {credits < CREDIT_COST && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }}
               onClick={() => navigate('/subscription')}>
               Insufficient credits — <u>upgrade</u>
            </p>
          )}
        </div>
      </section>

      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}><Icons.X sz={13} /></button>
          <div className="mtab on"><Icons.Grid sz={13} /> MultiShot Grid</div>
          {isComplete && (
            <button className="mtab ms-zip-tab" onClick={downloadZip}>
              <Icons.DL sz={13} /> Download All (ZIP)
            </button>
          )}
          <div style={{ flex: 1 }} />
          <div className="mtab" onClick={() => navigate('/gallery')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.Grid sz={13} /> Creations
          </div>
        </div>

        <div className="mbody">
          {phase === 'idle' && (
            <div className="prev">
              <div className="prev-empty">
                <div className="ico"><Icons.Clapperboard sz={56} /></div>
                <div>Upload one image to create 9 cinematic angles.</div>
              </div>
            </div>
          )}

          {(phase === 'creating_grid' || phase === 'cropping') && (
            <div className="ms-progress-card">
              <div className="ms-step active">
                <span className={phase === 'creating_grid' ? 'spinner tiny' : 'ms-check'}>{phase === 'creating_grid' ? '' : <Icons.Check sz={13} />}</span>
                <div>
                  <strong>Creating cinematic storyboard...</strong>
                  {progress.gridUrl && <small>Grid saved</small>}
                </div>
              </div>
              <div className={`ms-step ${phase !== 'creating_grid' ? 'active' : ''}`}>
                <span className={phase === 'cropping' ? 'spinner tiny' : 'ms-dot'} />
                <div>
                  <strong>Cropping 9 angles: {progress.current || 0}/9</strong>
                  <small>{phase === 'cropping' ? 'Saving cropped panels' : 'Waiting for storyboard'}</small>
                  <div className="ms-progress-bar">
                    <div style={{ width: `${Math.min(100, ((progress.current || 0) / 9) * 100)}%` }} />
                  </div>
                </div>
              </div>
              {progress.creditsDeducted > 0 && (
                <div className="ms-credit-note">{progress.creditsDeducted} credits deducted</div>
              )}
              {error && <div className="ms-error">{error}</div>}
            </div>
          )}

          {phase === 'failed' && (
            <div className="prev">
              <div className="prev-empty">
                <div className="ico"><Icons.X sz={50} /></div>
                <div>{error || 'MultiShot failed'}</div>
              </div>
            </div>
          )}

          {isComplete && (
            <div className="ms-results-wrap">
              <div className="ms-result-head">
                <div>
                  <h2>MultiShot Results</h2>
                  <span>{progress.creditsDeducted || CREDIT_COST} credits deducted</span>
                </div>
                <button className="pabtn" onClick={downloadZip}><Icons.DL sz={14} /> Download All (ZIP)</button>
              </div>

              <div className="ms-result-grid" style={{ '--shot-aspect': shotAspect }}>
                {resultPanels.map((panel, idx) => {
                  const shotLabel = getShotLabel(progress.style || style, panel, idx);
                  return (
                  <div key={panel.index} className="ms-shot-card">
                    <button
                      className="prev-icon-btn ms-shot-download"
                      title="Download"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadFile(panel.url, token).catch(() => toast.error('Download failed'));
                      }}
                    >
                      <Icons.DL sz={14} />
                    </button>
                    <div className="ms-shot-media" onClick={() => setFullscreenPanel({ ...panel, label: shotLabel })}>
                      <img src={panel.url} alt={shotLabel} />
                    </div>
                    <div className="ms-shot-label">{String(idx + 1).padStart(2, '0')}. {shotLabel}</div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
